import React, { useState } from 'react';

import styles from './PasswordField.module.scss';
import { TextField } from '../textField/TextField';
import { Text } from '../../typography/text/Text';
import { CloseCross, Tick } from '../icons';
import clsx from 'clsx';
import Button from '../button';
import { showPasswordIcon } from './_vars';

const PasswordField = (props) => {
  const {
    error,
    errorMessage,
    register,
    inputStyle,
    name,
    noteStyle,
    onChange = () => {},
    onlyField,
    hideNote,
    iconClassName,
    iconAfter,
    noteSize,
    placement = 'default',
    ...fieldProps
  } = props;

  const [value, setValue] = useState();
  const [showPassword, setShowPassword] = useState(false);

  const passwordValidator = {
    min: (length, message) => ({ pattern: new RegExp(`^(.{${length},})$`, 'g'), message }),
    minNumbers: (count, message) => ({ pattern: new RegExp(`^(.*[0-9].{${count - 1},})$`, 'g'), message }),
    minSymbols: (count, message) => ({
      pattern: new RegExp(`^(?=.*[!@#$%^&*()_+\\-=\\[\\]{};':"\\|,.<>\\/\\?]).{${count},}$`),
      message
    }),
    minUppercase: (count, message) => ({ pattern: new RegExp(`^(.*[A-Z].{${count - 1},})$`, 'g'), message }),
    validate: (password, rules) => {
      const results = [];

      for (const rule of rules) {
        const { pattern, message } = rule;
        results.push({
          status: pattern.test(password),
          message: message,
        });
      }

      return results;
    },
  };

  const rules = [
    passwordValidator.minUppercase(1, 'an uppercase letter'),
    passwordValidator.minNumbers(1, 'number'),
    passwordValidator.minSymbols(1, 'special character'),
  ];

  const validationResult = passwordValidator.validate(value, rules);
  const validationRules = validationResult.sort((a, b) => {
    if (a.status === false && b.status === false) {
      return 0;
    } else if (a.status === false) {
      return -1;
    } else {
      return 1;
    }
  });

  const allValid = validationResult.every((item) => item.status);
  const allInvalid = validationResult.every((item) => !item.status);

  return (
    <>
      <TextField
        {...register(name, {
          onChange: (ev) => {
            setValue(ev.target.value);
            onChange && onChange();
          },
        })}
        type={showPassword ? 'text' : 'password'}
        className={inputStyle}
        error={error}
        errorMessage={null}
        {...fieldProps}
        iconAfter={
          <>
            <Button
              className={clsx(styles.showIcon, iconClassName)}
              variant="primary"
              as="text"
              onClick={() => {
                setShowPassword((prevState) => !prevState)
              }}
              disableTouchEvent
              ariaLabel="Show password"
            >
              {showPassword ? showPasswordIcon.show : showPasswordIcon.hide}
            </Button>
            {iconAfter}
          </>
        }
      />
      {!onlyField && (
        <>
          {errorMessage && (
            <div className={clsx(styles.errorsWrapper, styles[`placement_${placement}`])}>
              <Text className={clsx(styles.errorLabel, { [styles.valid]: allValid })} as="p" weight="light">
                {allInvalid ? 'You are missing:' : 'Something is missing:'}
              </Text>
              <div className={styles.errorsGrid}>
                {validationRules?.map((rule, idx) => (
                  <div key={idx} className={clsx(styles.rule, { [styles.valid]: rule.status })}>
              <span className={styles.icon}>
                {rule.status ? <Tick/> : <CloseCross/>}
              </span>
                    {rule.message}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!hideNote && (
            <Text className={noteStyle} as="p" size={noteSize} weight="light">
              *Password must be at least 8 characters long and contain at least 1
              uppercase letter, a number and a special character.
            </Text>
          )}
        </>
      )}
    </>
  );
};

export default PasswordField;
